/**
 * Candlestick + volume chart built on TradingView Lightweight Charts (Apache-2.0).
 *
 * Presentational only: it receives already-mapped series (see `candlestickChartData.ts`)
 * and never fetches anything, so it works for any TekInvest data source that can produce
 * `CandlePoint[]` / `VolumePoint[]`.
 *
 * Phase-2 extension points, already supported and unused in Phase 1:
 *   - `overlays`  -> extra line series on the price pane (e.g. EMA 20/50/200 via
 *                    `indicatorToLineOverlay`).
 *   - `markers`   -> markers on the candle series (e.g. real algo entry/exit events).
 *
 * Licence: Lightweight Charts requires visible attribution to TradingView. The library's
 * default attribution logo is left ENABLED here (do not set `attributionLogo: false`
 * unless an equivalent link is shown elsewhere on the page).
 */
import { useEffect, useRef } from "react";
import type { ComponentRef } from "react";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  CandlestickSeries,
  ColorType,
  HistogramSeries,
  LineSeries,
  createChart,
  createSeriesMarkers,
} from "lightweight-charts";
import type {
  IChartApi,
  ISeriesApi,
  ISeriesMarkersPluginApi,
  Time,
} from "lightweight-charts";
import {
  CANDLE_DOWN_COLOR,
  CANDLE_UP_COLOR,
  type CandlePoint,
  type ChartLineOverlay,
  type ChartMarker,
  type VolumePoint,
} from "./candlestickChartData";
import { formatZonedDateTime, formatZonedTick } from "./zonedTime";

export interface CandlestickVolumeChartProps {
  candles: readonly CandlePoint[];
  volume: readonly VolumePoint[];
  overlays?: readonly ChartLineOverlay[];
  markers?: readonly ChartMarker[];
  height?: number;
  /** Set false when the series has no usable volume: hides the volume pane (default true). */
  showVolume?: boolean;
  /** IANA zone for time labels when times are epoch seconds (intraday). Omit for daily bars. */
  timeZone?: string;
  /** Zoom to this time window (same time type as the data) instead of fitting everything. Omit to fit all. */
  visibleRange?: { from: number; to: number };
}

/** Pixels kept free at the bottom of the pane for the (required) TradingView attribution logo. */
const LOGO_STRIP_PX = 44;
/** Share of the pane height used by the volume bars (when shown). */
const VOLUME_PANE = 0.2;

const EMPTY_OVERLAYS: readonly ChartLineOverlay[] = [];
const EMPTY_MARKERS: readonly ChartMarker[] = [];

export default function CandlestickVolumeChart({
  candles,
  volume,
  overlays = EMPTY_OVERLAYS,
  markers = EMPTY_MARKERS,
  height = 380,
  showVolume = true,
  timeZone,
  visibleRange,
}: CandlestickVolumeChartProps) {
  const rangeFrom = visibleRange?.from;
  const rangeTo = visibleRange?.to;
  const theme = useTheme();
  const textColor = theme.palette.text.secondary;
  const gridColor = theme.palette.divider;
  const fontFamily = theme.typography.fontFamily;
  const containerRef = useRef<ComponentRef<"div"> | null>(null);
  const overlaySeriesRef = useRef(
    new Map<string, { series: ISeriesApi<"Line">; data: ChartLineOverlay["data"]; color: string; label: string }>()
  );
  const chartRef = useRef<IChartApi | null>(null);
  const candleSeriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const volumeSeriesRef = useRef<ISeriesApi<"Histogram"> | null>(null);
  const markersPluginRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null);

  // Create the chart once; dispose on unmount (also covers React 19 strict-mode double mount).
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    const overlayMap = overlaySeriesRef.current;

    const chart = createChart(container, {
      autoSize: true,
      height,
      layout: {
        background: { type: ColorType.Solid, color: "transparent" },
        textColor,
        fontFamily,
      },
      grid: {
        vertLines: { color: gridColor },
        horzLines: { color: gridColor },
      },
      rightPriceScale: { borderColor: gridColor },
      timeScale: { borderColor: gridColor },
    });

    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: CANDLE_UP_COLOR,
      downColor: CANDLE_DOWN_COLOR,
      borderUpColor: CANDLE_UP_COLOR,
      borderDownColor: CANDLE_DOWN_COLOR,
      wickUpColor: CANDLE_UP_COLOR,
      wickDownColor: CANDLE_DOWN_COLOR,
    });
    // Layout of the pane (top to bottom): headroom so the top price label is not clipped,
    // candles, volume, then a strip kept EMPTY for the TradingView attribution logo (which the
    // library pins bottom-left and which must stay visible).
    candleSeries.priceScale().applyOptions({ scaleMargins: { top: 0.12, bottom: 0.32 } });

    // Volume on its own (overlay) price scale so its magnitude never distorts the price axis.
    const volumeSeries = chart.addSeries(HistogramSeries, {
      priceFormat: { type: "volume" },
      priceScaleId: "volume",
      lastValueVisible: false,
      priceLineVisible: false,
    });
    volumeSeries.priceScale().applyOptions({ scaleMargins: { top: 0.72, bottom: 0.1 } });

    chartRef.current = chart;
    candleSeriesRef.current = candleSeries;
    volumeSeriesRef.current = volumeSeries;

    return () => {
      chart.remove();
      chartRef.current = null;
      candleSeriesRef.current = null;
      volumeSeriesRef.current = null;
      markersPluginRef.current = null;
      overlayMap.clear();
    };
    // Created once; later theme/height changes are applied by the effects below, never by rebuilding.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Push data whenever the series change. Times are strings (daily "YYYY-MM-DD") or UTC epoch
  // seconds (intraday); both are valid library `Time` values, the cast only satisfies the branded type.
  useEffect(() => {
    const candleSeries = candleSeriesRef.current;
    const volumeSeries = volumeSeriesRef.current;
    const chart = chartRef.current;
    if (!candleSeries || !volumeSeries || !chart) return;
    candleSeries.setData(candles.map((c) => ({ ...c, time: c.time as Time })));
    volumeSeries.setData(showVolume ? volume.map((v) => ({ ...v, time: v.time as Time })) : []);
    chart.timeScale().fitContent();
  }, [candles, volume, showVolume]);

  // Zoom window (e.g. "focus the trade"); without one the whole series is fitted.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    if (rangeFrom !== undefined && rangeTo !== undefined && rangeFrom < rangeTo) {
      chart.timeScale().setVisibleRange({ from: rangeFrom as Time, to: rangeTo as Time });
    } else {
      chart.timeScale().fitContent();
    }
  }, [rangeFrom, rangeTo, candles]);

  // Volume pane on/off. When there is no usable volume (e.g. NIFTY spot reports 0) the pane is
  // hidden and its space handed back to the candles, instead of drawing an empty/flat strip.
  // The bottom strip is sized in PIXELS (as a fraction of the current height) so the TradingView
  // logo never sits on the volume bars, however short the chart is.
  useEffect(() => {
    const candleSeries = candleSeriesRef.current;
    const volumeSeries = volumeSeriesRef.current;
    if (!candleSeries || !volumeSeries) return;
    const strip = Math.min(0.3, Math.max(0.1, LOGO_STRIP_PX / Math.max(height, 1)));
    volumeSeries.applyOptions({ visible: showVolume });
    volumeSeries.priceScale().applyOptions({ scaleMargins: { top: 1 - strip - VOLUME_PANE, bottom: strip } });
    candleSeries
      .priceScale()
      .applyOptions({ scaleMargins: showVolume ? { top: 0.12, bottom: strip + VOLUME_PANE + 0.04 } : { top: 0.12, bottom: strip } });
  }, [showVolume, height]);

  // Display timezone for intraday charts: data stays epoch seconds, only the labels are converted.
  useEffect(() => {
    if (!timeZone) return;
    chartRef.current?.applyOptions({
      localization: {
        timeFormatter: (t: Time) => (typeof t === "number" ? formatZonedDateTime(t, timeZone) : String(t)),
      },
      timeScale: {
        timeVisible: true,
        secondsVisible: false,
        tickMarkFormatter: (t: Time, tickMarkType: number) =>
          typeof t === "number" ? formatZonedTick(t, tickMarkType, timeZone) : null,
      },
    });
  }, [timeZone]);

  // Theme / height changes are applied to the live chart (no rebuild).
  useEffect(() => {
    chartRef.current?.applyOptions({
      layout: { textColor, fontFamily },
      grid: { vertLines: { color: gridColor }, horzLines: { color: gridColor } },
      rightPriceScale: { borderColor: gridColor },
      timeScale: { borderColor: gridColor },
    });
  }, [textColor, gridColor, fontFamily]);

  useEffect(() => {
    // With autoSize the container's own height (below) drives the size; this also covers
    // the library's fixed-size fallback.
    chartRef.current?.applyOptions({ height });
  }, [height]);

  // Indicator overlays: reconciled by id, so toggling one line adds/removes only that
  // series and unchanged lines (same id + same data array) are never recreated.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const live = overlaySeriesRef.current;
    const wanted = new Set(overlays.map((o) => o.id));
    for (const [id, entry] of live) {
      if (!wanted.has(id)) {
        chart.removeSeries(entry.series);
        live.delete(id);
      }
    }
    for (const overlay of overlays) {
      const entry = live.get(overlay.id);
      if (!entry) {
        const series = chart.addSeries(LineSeries, {
          color: overlay.color,
          lineWidth: 2,
          title: overlay.label,
          priceLineVisible: false,
          lastValueVisible: false,
        });
        series.setData(overlay.data.map((p) => ({ ...p, time: p.time as Time })));
        live.set(overlay.id, { series, data: overlay.data, color: overlay.color, label: overlay.label });
        continue;
      }
      if (entry.color !== overlay.color || entry.label !== overlay.label) {
        entry.series.applyOptions({ color: overlay.color, title: overlay.label });
        entry.color = overlay.color;
        entry.label = overlay.label;
      }
      if (entry.data !== overlay.data) {
        entry.series.setData(overlay.data.map((p) => ({ ...p, time: p.time as Time })));
        entry.data = overlay.data;
      }
    }
  }, [overlays]);

  // Optional markers (Phase 2): real trade events, time-positioned only.
  useEffect(() => {
    const candleSeries = candleSeriesRef.current;
    if (!candleSeries) return;
    const mapped = markers.map((m) => ({ ...m, time: m.time as Time }));
    if (markersPluginRef.current) {
      markersPluginRef.current.setMarkers(mapped);
    } else if (mapped.length > 0) {
      markersPluginRef.current = createSeriesMarkers(candleSeries, mapped);
    }
  }, [markers]);

  return <Box ref={containerRef} data-testid="candlestick-volume-chart" sx={{ width: "100%", height }} />;
}
